import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { Navigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "../../components/Admin/Sidebar";
import StaffPanel from "../../components/Admin/People/StaffPanel";
import MembersPanel from "../../components/Admin/People/MembersPanel";
import MembershipsPanel from "../../components/Admin/Memberships/MembershipsPanel";
import StoreSectionsPanel from "../../components/Admin/Store/Sections/StoreSectionsPanel";
import StoreCategoriesPanel from "../../components/Admin/Store/Categories/StoreCategoriesPanel";
import StoreBrandsPanel from "../../components/Admin/Store/Brands/StoreBrandsPanel";
import StoreProductsPanel from "../../components/Admin/Store/Products/StoreProductsPanel";

export default function AdminDashboard() {
  const { authLoading, isAuthenticated, isAdmin } = useAuth();
  const { section } = useParams();

  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f5f7fa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Paper
          sx={{
            width: "100%",
            maxWidth: 420,
            borderRadius: "24px",
            p: 3,
            bgcolor: "#ffffff",
            border: "1px solid #e5e7eb",
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          }}
        >
          <Typography
            sx={{
              color: "#111827",
              fontSize: 18,
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            Cargando panel
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/admin-login" replace />;
  }

  const allowedSections = [
    "staff",
    "members",
    "memberships",
    "store-sections",
    "store-categories",
    "store-brands",
    "store-products",
    "store-orders",
  ];

  const normalizedSection = allowedSections.includes(section)
    ? section
    : "members";

  const sectionTitleMap = {
    staff: "Gestión de staff",
    members: "Gestión de miembros",
    memberships: "Gestión de membresías",
    "store-sections": "Secciones de la tienda",
    "store-categories": "Categorías de la tienda",
    "store-brands": "Marcas de la tienda",
    "store-products": "Productos de la tienda",
    "store-orders": "Órdenes de la tienda",
  };

  const sectionSubtitleMap = {
    staff: "Administra cuentas internas del gimnasio.",
    members: "Controla miembros, estados y accesos.",
    memberships: "Crea, edita y administra las membresías del gimnasio.",
    "store-sections":
      "Organiza la estructura principal del catálogo de NovaXFit Store.",
    "store-categories":
      "Agrupa los productos por categoría dentro de cada sección.",
    "store-brands":
      "Administra las marcas globales disponibles para el catálogo.",
    "store-products":
      "Crea productos, variantes, imágenes y disponibilidad ecommerce.",
    "store-orders":
      "Controla los pedidos realizados desde la tienda online.",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        display: "flex",
      }}
    >
      <Sidebar />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          ml: { xs: 0, lg: "220px" },
          px: { xs: 1.2, md: 2, xl: 2.6 },
          py: { xs: 1.2, md: 1.8, xl: 2.2 },
        }}
      >
        <Stack spacing={1.8}>
          <Paper
            sx={{
              borderRadius: "24px",
              px: { xs: 1.4, md: 2.2 },
              py: { xs: 1.4, md: 2 },
              border: "1px solid #e5e7eb",
              bgcolor: "#ffffff",
              boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: "#111827",
                    fontSize: { xs: 22, md: 26 },
                    fontWeight: 800,
                  }}
                >
                  {sectionTitleMap[normalizedSection]}
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 13.2,
                    fontWeight: 500,
                    mt: 0.7,
                  }}
                >
                  {sectionSubtitleMap[normalizedSection]}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Box sx={{ width: "100%" }}>
            {normalizedSection === "staff" && <StaffPanel />}
            {normalizedSection === "members" && <MembersPanel />}
            {normalizedSection === "memberships" && <MembershipsPanel />}
            {normalizedSection === "store-sections" && <StoreSectionsPanel />}
            {normalizedSection === "store-categories" && <StoreCategoriesPanel />}
            {normalizedSection === "store-brands" && <StoreBrandsPanel />}
            {normalizedSection === "store-products" && <StoreProductsPanel />}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}